import classnames from "classnames";
import React, { useEffect, useState } from "react";
import CheckboxFilterItem from "web/react/components/checkbox-filter-item/checkbox-filter-item";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { StandardFilterProps } from "web/types/feed-filters";
import { FilteredScrollAreaFilterOptionGroup } from "web/types/universal-filters";
import styles from "./filter-option-group.css";

const MAX_TO_DISPLAY = 50;

interface FilterOptionGroupProps extends StandardFilterProps {
    isVisible: boolean;
    optionGroup: FilteredScrollAreaFilterOptionGroup;
    lazyFacetFetch: (optionGroupId: string) => void;
    lazyFacetIsLoading: boolean;
}

function FilterOptionGroup({
    isVisible,
    onClick,
    optionGroup,
    pendingFilterInfo,
    lazyFacetFetch,
    lazyFacetIsLoading,
}: FilterOptionGroupProps): JSX.Element {
    const shouldLoadOptions = optionGroup.lazy_loaded && isVisible;
    useEffect(() => {
        if (shouldLoadOptions) {
            // rothko caching ensures we don't duplicate calls, e.g. if a filter is
            // added and then removed again
            lazyFacetFetch(optionGroup.id);
        }
    }, [lazyFacetFetch, shouldLoadOptions, optionGroup.id]);

    let [query, setQuery] = useState("");

    let toRender = optionGroup.options
        .filter((option) => query == "" || option.label.toLowerCase().includes(query))
        .filter((option, idx) => idx < MAX_TO_DISPLAY);

    function handleFilter(event: React.ChangeEvent<HTMLInputElement>): void {
        setQuery(event.target.value.toLowerCase());
    }

    let { id, search_placeholder: placeholder } = optionGroup;

    return (
        <div
            className={classnames(styles.background, {
                [styles.loading]: lazyFacetIsLoading,
            })}
        >
            <div className={styles.searchField}>
                <input
                    className={styles.input}
                    type="text"
                    onChange={handleFilter}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    aria-controls={`search-result_${id}`}
                />
                <div className={styles.searchIcon}>
                    <SVGIcon name="round-search" />
                </div>
            </div>
            <ol
                className={styles.results}
                role="region"
                id={`search-result_${id}`}
                aria-live="polite"
                aria-atomic="false"
            >
                {toRender.map((item) => {
                    return (
                        <li id={`${id}-${item.value}`} key={`${id}-${item.value}`}>
                            <CheckboxFilterItem
                                {...{ item, onClick, pendingFilterInfo }}
                                // TODO: is it?
                                isSmallItem={true}
                            />
                        </li>
                    );
                })}
            </ol>
            {lazyFacetIsLoading && <LoadingSpinner />}
        </div>
    );
}

export default FilterOptionGroup;
